import { ChevronDown } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import { useT } from "@/lib/i18n";
import { cn, storage } from "@/lib/utils";

/**
 * Progressive disclosure: a `button[aria-expanded]` that shows or hides a region.
 * Default label "Advanced" / "خيارات متقدمة"; pass `kind="technical"` for "Technical details" / "تفاصيل تقنية".
 * `rememberKey` keeps the open state on this device (e.g. "rules.builder.advanced").
 */
export function Disclosure({
  label,
  kind = "advanced",
  children,
  defaultOpen = false,
  open: controlled,
  onOpenChange,
  rememberKey,
  className,
  testId,
}: {
  label?: ReactNode;
  kind?: "advanced" | "technical";
  children: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  rememberKey?: string;
  className?: string;
  testId?: string;
}) {
  const { t } = useT();
  const id = useId();
  const key = rememberKey ? `malfak.disclosure.${rememberKey}` : null;
  const [inner, setInner] = useState(() => {
    const saved = key ? storage.get(key) : null;
    return saved === null ? defaultOpen : saved === "1";
  });
  const open = controlled ?? inner;
  const toggle = () => {
    const next = !open;
    if (controlled === undefined) setInner(next);
    if (key) storage.set(key, next ? "1" : "0");
    onOpenChange?.(next);
  };
  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={toggle}
        data-testid={testId}
        className="inline-flex items-center gap-1.5 rounded-lg py-1 text-[13px] font-medium text-ink-2 hover:text-ink"
      >
        <ChevronDown className={cn("size-4 transition-transform duration-200", !open && "ltr:-rotate-90 rtl:rotate-90")} aria-hidden />
        {label ?? (kind === "technical" ? t("kit.technicalDetails") : t("kit.advanced"))}
      </button>
      <div id={id} role="region" hidden={!open} className="pt-3">
        {open && children}
      </div>
    </div>
  );
}
